import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Loader2, Check } from 'lucide-react';
import { COLOR_OJO } from '../../lib/oftalmo';

/**
 * Apuntar sin abrir ventanas (4/11/2026): «simplifica el número de clics…
 * pon tensión ocular OD, OI, ya está, y que se escriba».
 *
 * Una fila con las casillas de lo que se mide en cada consulta; se escribe,
 * Intro (o «Guardar») y queda apuntado con la fecha de hoy. Los números se
 * vacían para la siguiente; los desplegables (método, tipo, fármaco) se
 * quedan como estaban.
 *
 * `campos` = [{ k: 'od.valor', etiqueta: 'OD', ancho: 'w-16' } |
 *             { k: 'metodo', etiqueta: 'Método', opciones: [...], nombres? }]
 */
const poner = (obj, ruta, valor) => {
  const [a, b] = ruta.split('.');
  if (!b) return { ...obj, [a]: valor };
  return { ...obj, [a]: { ...(obj[a] || {}), [b]: valor } };
};
const leer = (obj, ruta) => {
  const [a, b] = ruta.split('.');
  return b ? obj[a]?.[b] : obj[a];
};

export default function EntradaRapida({ pid, tipo, titulo, campos, iniciales = {}, unidad, onGuardado, testId, sinNumeros = false }) {
  const [v, setV] = useState(() => {
    let o = {};
    campos.forEach((c) => { if (c.opciones) o = poner(o, c.k, iniciales[c.k] ?? c.opciones[0]); });
    return o;
  });
  const [guardando, setGuardando] = useState(false);
  const [hecho, setHecho] = useState(false);
  const primero = useRef(null);

  // `sinNumeros`: se guarda aunque no se escriba nada (una inyección es su ojo y su fármaco).
  const hayAlgo = sinNumeros || campos.some((c) => !c.opciones && !c.separador && String(leer(v, c.k) ?? '').trim());
  const guardar = async () => {
    if (!hayAlgo || guardando) return;
    setGuardando(true);
    try {
      const r = await api.post(`/api/patients/${pid}/oftalmologia/registros`,
        { tipo, fecha: new Date().toISOString().slice(0, 10), ...v });
      onGuardado?.(r.data);
      // Se vacían los números; los desplegables se quedan para la siguiente.
      let o = {};
      campos.forEach((c) => { if (c.opciones) o = poner(o, c.k, leer(v, c.k)); });
      setV(o);
      setHecho(true);
      setTimeout(() => setHecho(false), 1500);
      primero.current?.focus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };
  const tecla = (e) => { if (e.key === 'Enter') { e.preventDefault(); guardar(); } };
  let primerNumero = true;

  return (
    <div className="flex flex-wrap items-end gap-2 rounded-lg bg-[var(--cp-soft)] dark:bg-muted/30 px-3 py-2 mb-3" data-testid={testId}>
      {titulo && <div className="text-sm font-semibold self-center mr-1">{titulo}</div>}
      {campos.map((c) => {
        const ojo = c.etiqueta === 'OD' || c.etiqueta === 'OI' ? c.etiqueta : (c.k.startsWith('od.') ? 'OD' : c.k.startsWith('oi.') ? 'OI' : null);
        if (c.separador) return <span key={c.k} className="self-center h-6 w-px bg-border mx-1" />;
        if (c.opciones) {
          return (
            <label key={c.k} className="text-[11px] text-muted-foreground flex flex-col gap-0.5">
              {c.etiqueta}
              <select value={leer(v, c.k) ?? ''} onChange={(e) => setV((o) => poner(o, c.k, e.target.value))}
                className="h-8 rounded-md border border-input bg-background px-1.5 text-sm text-foreground">
                {c.opciones.map((x) => <option key={x} value={x}>{c.nombres?.[x] || x}</option>)}
              </select>
            </label>
          );
        }
        const ref = primerNumero ? primero : undefined;
        primerNumero = false;
        return (
          <label key={c.k} className="text-[11px] flex flex-col gap-0.5" style={{ color: ojo ? COLOR_OJO[ojo] : undefined }}>
            <span className={ojo ? 'font-semibold' : 'text-muted-foreground'}>{c.etiqueta}</span>
            <input ref={ref} inputMode={c.texto ? 'text' : 'decimal'} value={leer(v, c.k) ?? ''} onKeyDown={tecla}
              onChange={(e) => setV((o) => poner(o, c.k, e.target.value))} placeholder={c.ph || ''}
              className={`h-8 ${c.ancho || 'w-16'} rounded-md border border-input bg-background px-2 text-sm text-foreground`}
              data-testid={testId ? `${testId}-${c.k}` : undefined} />
          </label>
        );
      })}
      {unidad && <span className="text-xs text-muted-foreground self-center pb-1.5">{unidad}</span>}
      <Button size="sm" className="h-8" onClick={guardar} disabled={!hayAlgo || guardando} data-testid={testId ? `${testId}-guardar` : undefined}>
        {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : hecho ? <Check className="w-4 h-4" /> : 'Guardar'}
      </Button>
      <span className="text-[11px] text-muted-foreground self-center pb-1.5">Intro para guardar · hoy</span>
    </div>
  );
}
